import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { ordersAPI } from '../../api/orders';
import type { Order } from '../../types';
import {
  CheckCircle,
  ShoppingBag,
  Truck,
  Home,
  Package,
  Printer,
  CreditCard,
  Wallet,
  Clock,
  User,
  MapPin,
  Phone,
  Mail,
  Smartphone,
  Banknote,
  QrCode,
  AlertCircle,
  XCircle,
  Circle,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { useOrders } from '../../contexts/OrdersContext';
import { formatPrice } from '../../utils/utilityFunctions';

const OrderConfirmation: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { getOrder } = useOrders();
  const [isTobagoCity, setIsTobagoCity] = useState(false);

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetails();
    }
  }, [orderNumber]);

  const fetchOrderDetails = async () => {
    setIsLoading(true);
    setError('');
    try {
      const order = await getOrder(orderNumber!);
      if (order) {
        setOrder(order);
        // Check if city is in Tobago (simplified check - will need proper zone logic)
        const tobagoCities = ['scarborough', 'crown point', 'roxborough'];
        const cityName = order.shipping_city_name?.toLowerCase() || '';
        setIsTobagoCity(tobagoCities.some(city => cityName.includes(city)));
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError('Failed to load order details');
      console.error('Error fetching order:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('printable-receipt');
    const originalContent = document.body.innerHTML;

    if (printContent) {
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload(); // Restore original page
    } else {
      window.print();
    }
  };
  
  const getProgressHeight = (status) => {
    const statusMap = {
      'confirmed': '25%',
      'packed': '50%',
      'out_for_delivery': '75%',
      'delivered': '100%',
      'cancelled': '0%',
      'refunded': '0%'
    };
    return statusMap[status] || '0%';
  };

  const getStatusIndex = (status) => {
    const statusOrder = ['confirmed', 'packed', 'out_for_delivery', 'delivered'];
    return statusOrder.indexOf(status);
  };

  const getEstimatedTime = (status) => {
    const estimates = {
      'confirmed': 'Packaging your order soon',
      'packed': 'Shipping within 48 hours',
      'out_for_delivery': 'Delivery by end of day',
      'delivered': 'Successfully delivered'
    };
    return estimates[status] || '';
  };

  // Update your getStatusIcon to include icons for all statuses
  const getStatusIcon = (status) => {
    const icons = {
      'confirmed': <CheckCircle className="h-8 w-8 text-green-500" />,
      'packed': <Package className="h-8 w-8 text-blue-500" />,
      'out_for_delivery': <Truck className="h-8 w-8 text-orange-500" />,
      'delivered': <Home className="h-8 w-8 text-green-600" />,
      'cancelled': <XCircle className="h-8 w-8 text-red-500" />,
      // 'refunded': <RefreshCw className="h-8 w-8 text-purple-500" />
    };
    return icons[status] || <Clock className="h-8 w-8 text-gray-400" />;
  };
  // const getStatusIcon = (status: string) => {
  //   switch (status) {
  //     case 'delivered':
  //       return <CheckCircle className="h-6 w-6 text-green-600" />;
  //     case 'out_for_delivery':
  //       return <Truck className="h-6 w-6 text-blue-500" />;
  //     case 'packed':
  //       return <Package className="h-6 w-6 text-orange-500" />;
  //     case 'confirmed':
  //       return <Clock className="h-6 w-6 text-yellow-500" />;
  //     case 'cancelled':
  //       return <AlertCircle className="h-6 w-6 text-red-500" />;
  //     default:
  //       return <Clock className="h-6 w-6 text-gray-500" />;
  //   }
  // };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Order Confirmed';
      case 'packed':
        return 'Packed & Ready';
      case 'out_for_delivery':
        return 'Out for Delivery';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Processing';
    }
  };

  const getPaymentMethodDetails = (method: string) => {
    switch (method) {
      case 'cash_on_delivery':
        return { icon: Banknote, text: 'Cash on Delivery', color: 'text-green-600' };
      case 'cash_in_person':
        return { icon: Wallet, text: 'Cash (In Person)', color: 'text-green-600' };
      case 'credit_card_on_delivery':
        return { icon: CreditCard, text: 'Credit Card on Delivery', color: 'text-blue-600' };
      case 'debit_card_on_delivery':
        return { icon: CreditCard, text: 'Debit Card on Delivery', color: 'text-blue-600' };
      case 'credit_card_online':
        return { icon: CreditCard, text: 'Credit Card (Online)', color: 'text-purple-600' };
      case 'debit_card_online':
        return { icon: CreditCard, text: 'Debit Card (Online)', color: 'text-purple-600' };
      case 'bank_transfer':
        return { icon: QrCode, text: 'Bank Transfer', color: 'text-indigo-600' };
      default:
        return { icon: CreditCard, text: method, color: 'text-gray-600' };
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="h-48 bg-gray-200 rounded-lg mb-6"></div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {error || 'Order not found'}
          </h3>
          <div className="mt-6">
            <Link
              to="/products"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const paymentDetails = getPaymentMethodDetails(order.payment_method);
  const PaymentIcon = paymentDetails.icon;
  const isPaid = order.payment_status === 'paid' || order.status === 'delivered';

  // Printable Receipt Content
  const PrintableReceipt = () => (
    <div id="printable-receipt" className="print-only p-8 max-w-md mx-auto">
      {/* Company Header */}
      <div className="text-center mb-6">
        <div className="flex justify-center mb-2">
          {/* Logo placeholder */}
          <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
        </div>
        <h1 className="text-xl font-bold">Wright Tech Depot</h1>
        <p className="text-sm text-gray-600">POS Receipt</p>
      </div>

      {/* Order Info */}
      <div className="border-b border-gray-300 pb-4 mb-4">
        <div className="flex justify-between text-sm">
          <span>Order #{order.order_number}</span>
          <span>{format(new Date(order.created_at), 'dd/MM/yy HH:mm')}</span>
        </div>
        <div className="text-xs text-gray-600 mt-1">
          Status: {getStatusText(order.status)}
        </div>
      </div>

      {/* Items */}
      <div className="mb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300">
              <th className="text-left py-1">Item</th>
              <th className="text-center py-1">Qty</th>
              <th className="text-right py-1">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items?.map((item) => (
              <tr key={item.id} className="border-b border-gray-200">
                <td className="py-2">
                  <div className="font-medium">{item.product_name || 'Item'}</div>
                  <div className="text-xs text-gray-600">{item.product_variant_name}</div>
                </td>
                <td className="text-center py-2">×{item.quantity}</td>
                <td className="text-right py-2 font-medium">{formatPrice(item.total_price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="border-t border-gray-300 pt-4">
        <div className="flex justify-between text-sm py-1">
          <span>Subtotal</span>
          <span>{formatPrice(order.subtotal)}</span>
        </div>
        {order.delivery_fee > 0 && (
          <div className="flex justify-between text-sm py-1">
            <span>Delivery</span>
            <span>{formatPrice(order.delivery_fee)}</span>
          </div>
        )}
        {order.discount_total > 0 && (
          <div className="flex justify-between text-sm py-1">
            <span>Discount</span>
            <span className="text-green-600">-{formatPrice(order.discount_total)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-base border-t border-gray-300 mt-2 pt-2">
          <span>TOTAL</span>
          <span>{formatPrice(order.total)}</span>
        </div>
      </div>

      {/* Payment */}
      <div className="border-t border-gray-300 mt-4 pt-4 text-sm">
        <div className="flex justify-between py-1">
          <span className="font-medium">Payment:</span>
          <span>{paymentDetails.text}</span>
        </div>
        {order.cash_received && (
          <div className="flex justify-between py-1">
            <span>Cash Received:</span>
            <span>{formatPrice(order.cash_received)}</span>
          </div>
        )}
        {order.cash_change && (
          <div className="flex justify-between py-1">
            <span>Change:</span>
            <span>{formatPrice(order.cash_change)}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-600 mt-8 pt-4 border-t border-gray-300">
        <p>Thank you for your purchase!</p>
        <p className="mt-1">Average delivery: 2-3 business days</p>
      </div>
    </div>
  );
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Success Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {isPaid ? 'Payment Confirmed!' : 'Order Confirmed!'}
        </h1>
        <p className="text-gray-600">
          {isPaid
            ? 'Thank you for your payment. Your order is being processed.'
            : 'Thank you for your order. We will contact you soon.'
          }
        </p>
        <div className="mt-3 inline-flex items-center px-4 py-2 bg-gray-100 rounded-full">
          <span className="text-sm font-medium text-gray-700">
            Order #{order.order_number}
          </span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        <button
          onClick={handlePrint}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Printer className="mr-2 h-4 w-4" />
          {isPaid ? 'Print Receipt' : 'Print Invoice'}
        </button>
        <Link
          to="/products"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
        >
          <ShoppingBag className="mr-2 h-4 w-4" />
          Continue Shopping
        </Link>
      </div>

      {/* Tobago Bank Transfer Notice */}
      {isTobagoCity && order.payment_method === 'bank_transfer' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Bank Transfer Required (Tobago Orders)</h4>
              <p className="text-sm text-blue-700 mb-2">
                For Tobago deliveries, please complete payment via bank transfer and email the receipt to us.
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>1. Transfer total amount: <strong>{formatPrice(order.total)}</strong></li>
                <li>2. Email screenshot to: <strong>payments@wrighttechdepot.com</strong></li>
                <li>3. Include Order #: <strong>{order.order_number}</strong></li>
                <li>4. We'll confirm payment within 2 hours</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Summary */}
        <div className="bg-white rounded-lg border p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Order Summary
          </h2>
          {order.delivery_option && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Delivery Method</span>
                <span className="capitalize font-medium">{order.delivery_option}</span>
              </div>
            </div>
          )}
          {/* Items List - Grouped by Shipment */}
          <div className="space-y-4 mb-5">
            {order.shipments?.map((shipment, idx) => {
              console.log(shipment);
             return (
              <div key={shipment.id} className="border rounded-lg overflow-hidden">
                {/* Shipment Header - Stack on mobile */}
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center justify-between sm:justify-start gap-2">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-medium">
                          {order.delivery_option === 'economic' && idx === 0
                            ? 'Consolidated Shipment'
                            : `Shipment ${idx + 1}`}
                        </span>
                      </div>
                      {shipment.tracking_number && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                          Tracked
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between sm:justify-end items-center gap-2">
                      <span className="text-xs text-gray-500 sm:hidden">Ships from:</span>
                      <span className="text-xs sm:text-sm font-medium truncate max-w-[200px] sm:max-w-none">
                        {shipment.warehouse_name}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Items in Order */}
                <div className="divide-y divide-gray-100">
                  {shipment.items?.map((item, itemIdx) => (
                    <div key={itemIdx} className="flex gap-4 py-3 px-4 border-b border-gray-100 last:border-0">
                      {/* Product Image */}
                      <div className="w-16 h-16 flex-shrink-0">
                        {item.product_image ? (
                          <img
                            src={item.product_image}
                            alt={item.product_name}
                            loading="lazy"
                            className="w-full h-full object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Product Details */}
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">{item.product_name}</p>
                            {item.product_variant_name && (
                              <p className="text-sm text-gray-500">{item.product_variant_name}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {formatPrice(item.price * item.quantity)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatPrice(item.price)} each
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Qty: {item.quantity}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
            }
            )}
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-4">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatPrice(order.subtotal)}</span>
              </div>
              {order.delivery_fee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery Fee</span>
                  <span className="font-medium">{formatPrice(order.delivery_fee)}</span>
                </div>
              )}
              {order.discount_total > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Discount</span>
                  <span className="font-medium text-green-600">-{formatPrice(order.discount_total)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-300 pt-3 font-bold text-lg">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center">
                <PaymentIcon className={`h-5 w-5 mr-2 ${paymentDetails.color}`} />
                <div>
                  <p className="font-medium text-gray-900">{paymentDetails.text}</p>
                  <p className="text-sm text-gray-500">
                    {order.payment_status === 'paid' ? 'Paid' :
                      order.payment_method.includes('cash') || order.payment_method.includes('card_on_delivery')
                        ? 'Pay on Delivery' : 'Payment Required'}
                  </p>
                </div>
              </div>
              {order.payment_method.includes('card_on_delivery') && (
                <p className="text-sm text-gray-600 mt-2">
                  Driver will bring card machine for payment
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Status & Customer Info */}
        <div className="space-y-6">
          {/* Status - Modern Timeline */}
          <div className="bg-white rounded-lg border p-5 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Order Status</h2>

            <div className="flex items-start mb-6">
              <div className="flex-shrink-0">
                {getStatusIcon(order.status)}
              </div>
              <div className="ml-3 flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-gray-900 text-base">{getStatusText(order.status)}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {format(new Date(order.created_at), 'MMM d, yyyy • h:mm a')}
                    </p>
                  </div>
                  {order.tracking_number && (
                    <div className="mt-2 md:mt-0">
                      <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 border border-blue-100">
                        <Package className="h-3.5 w-3.5 text-blue-600 mr-2" />
                        <span className="text-xs font-medium text-blue-700">Tracked</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Visual Timeline */}
            <div className="relative">
              {/* Progress line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5">
                <div className="absolute inset-0 bg-gray-200"></div>
                <div
                  className="absolute inset-0 bg-green-500 transition-all duration-500"
                  style={{
                    height: getProgressHeight(order.status)
                  }}
                ></div>
              </div>

              {/* Timeline steps */}
              <div className="space-y-8 relative">
                {[
                  {
                    key: 'confirmed',
                    label: 'Order Confirmed',
                    description: 'We\'ve received your order',
                    icon: <CheckCircle className="h-5 w-5" />
                  },
                  {
                    key: 'packed',
                    label: 'Packed & Ready',
                    description: 'Items are prepared for shipping',
                    icon: <Package className="h-5 w-5" />
                  },
                  {
                    key: 'out_for_delivery',
                    label: 'Out for Delivery',
                    description: 'On the way to your location',
                    icon: <Truck className="h-5 w-5" />
                  },
                  {
                    key: 'delivered',
                    label: 'Delivered',
                    description: 'Successfully delivered',
                    icon: <Home className="h-5 w-5" />
                  }
                ].map((step, index) => {
                  const isActive = ['confirmed', 'packed', 'out_for_delivery', 'delivered']
                    .indexOf(order.status) >= index;
                  const isCurrent = getStatusIndex(order.status) === index;

                  return (
                    <div key={step.key} className="flex items-start">
                      {/* Timeline dot */}
                      <div className={`relative z-10 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isActive
                        ? 'bg-green-500 text-white shadow-lg shadow-green-100'
                        : 'bg-gray-100 text-gray-400'
                        } ${isCurrent ? 'ring-4 ring-green-100' : ''}`}>
                        {isActive ? step.icon : <Circle className="h-3 w-3" />}
                      </div>

                      {/* Step content */}
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'
                            }`}>
                            {step.label}
                          </p>
                          {isCurrent && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Current
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${isActive ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                          {step.description}
                        </p>

                        {/* Estimated time for current step */}
                        {isCurrent && getEstimatedTime(order.status) && (
                          <div className="mt-2 inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {getEstimatedTime(order.status)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Tracking Information */}
            {order.tracking_number && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-2">Tracking Number</p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                        <p className="text-gray-900 font-mono text-sm sm:text-base truncate">
                          {order.tracking_number}
                        </p>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(order.tracking_number)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                        title="Copy tracking number"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => window.open(`https://tracking.example.com/${order.tracking_number}`, '_blank')}
                    className="w-full sm:w-auto px-5 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Track Package
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Customer Info */}
          <div className="bg-white rounded-lg border p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Details</h2>
            <div className="space-y-3">
              <div className="flex items-start">
                <User className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{order.customer_name}</p>
                  <p className="text-sm text-gray-500">Customer</p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{order.customer_phone}</p>
                  <p className="text-sm text-gray-500">Phone</p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">{order.shipping_address_line_1}</p>
                  {order.shipping_city_name && (
                    <p className="text-sm text-gray-500">{order.shipping_city_name}</p>
                  )}
                </div>
              </div>
              {order.shipping_delivery_note && (
                <div className="text-sm bg-gray-50 p-3 rounded border">
                  <p className="font-medium text-gray-700">Delivery Note:</p>
                  <p className="text-gray-600">{order.shipping_delivery_note}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Info */}
      <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-5">
        <h3 className="font-medium text-blue-900 mb-3">Delivery Information</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <Clock className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>Average delivery time: <strong>2-3 business days</strong></span>
          </li>
          <li className="flex items-start">
            <Phone className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>Driver will contact you <strong>30-60 minutes before delivery</strong></span>
          </li>
          {order.payment_method.includes('cash') || order.payment_method.includes('card_on_delivery') ? (
            <li className="flex items-start">
              <Wallet className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Pay with <strong>cash or card</strong> when you receive your items</span>
            </li>
          ) : order.payment_method.includes('online') ? (
            <li className="flex items-start">
              <CreditCard className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>Your payment has been processed online</span>
            </li>
          ) : null}
          <li className="flex items-start">
            <Smartphone className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>You'll receive updates via <strong>WhatsApp & Email</strong></span>
          </li>
        </ul>
      </div>

      {/* Minimal Contact Info */}
      <div className="mt-6 text-center text-sm text-gray-600">
        <p>Questions about your order?</p>
        <div className="flex justify-center space-x-4 mt-2">
          <a href="mailto:support@wrighttechdepot.com" className="flex items-center text-primary-600 hover:text-primary-700">
            <Mail className="h-4 w-4 mr-1" />
            Email
          </a>
          <a href="https://wa.me/18681234567" className="flex items-center text-primary-600 hover:text-primary-700">
            <Smartphone className="h-4 w-4 mr-1" />
            WhatsApp
          </a>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-8 text-center">
        <Link
          to="/products"
          className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
        >
          <Home className="mr-2 h-4 w-4" />
          Return to Homepage
        </Link>
        <p className="mt-3 text-sm text-gray-500">
          You can track your order status anytime in your account
        </p>
      </div>

      {/* Hidden Printable Receipt */}
      <div className="hidden">
        <PrintableReceipt />
      </div>

      {/* Print Styles */}
      <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-receipt, #printable-receipt * {
              visibility: visible;
            }
            #printable-receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
          .print-only {
            display: none;
          }
          @media print {
            .print-only {
              display: block;
            }
          }
        `}</style>
    </div>
  );
};

export default OrderConfirmation;